// Third-party
import React, { Component } from 'react';
import { withFormik, FormikProps, Form } from 'formik';
import * as Yup from 'yup';

// Components
import FormInput from './FormInput';
import { PrimaryButton, CancelButton } from '../buttons';

// Utils
import { formatMessage, FormattedMessage } from '../../../utils/i18n';
import notification from '../../../utils/notification';
import EnterpriseMember from '@/types/enterprise-member';
import User from '@/types/user';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';

interface ParentProps {
  user: User;
  onModifyMember: (_id: string, uid?: string, nameId?: string) => Promise<void>;
  onCancel: () => void;
  modifyButtonTextKey: string;
  successTextKey: string;
  memberToUpdate: EnterpriseMember;
}

interface FormProps {
  fname?: string;
  lname?: string;
  email: string;
  uid?: string;
  nameId?: string;
}

type Props = ParentProps & FormikProps<FormProps>;

class ModifyEnterpriseMemberForm extends Component<Props> {
  static defaultProps = {
    onCancel: () => {},
  };

  canSubmit = () => {
    const { values, initialValues, isSubmitting, errors } = this.props;
    const isDirty = values !== initialValues;
    const hasErrors = Object.keys(errors).length > 0;

    // Allow adding but don't allow removing either UID or NameID
    const emptyUid = initialValues.uid && !values.uid;
    const emptyNameId = initialValues.nameId && !values.nameId;

    return !emptyUid && !emptyNameId && !hasErrors && isDirty && !isSubmitting;
  };

  render() {
    const { modifyButtonTextKey, memberToUpdate, user, initialValues } = this.props;

    return (
      <div className="settings-enterprise-add-member section align-items-center">
        <Form>
          <div className="fields-group">
            <div className="field">
              <label>{formatMessage('Admin.Enterprise.Fields.FirstName')}</label>
              <span className="readonly-field">{memberToUpdate.fname}</span>
            </div>

            <div className="field">
              <label>{formatMessage('Admin.Enterprise.Fields.LastName')}</label>
              <span className="readonly-field">{memberToUpdate.lname}</span>
            </div>
          </div>

          <div className="field-wide">
            <label>{formatMessage('Admin.Enterprise.Fields.Email')}</label>
            <span className="readonly-field">{memberToUpdate.email}</span>
          </div>

          {userHasPermission(user, PERMISSIONS.ADMIN_SECTION_SHOW_USER_UID) && (
            <FormInput
              name="uid"
              required={!!initialValues.uid}
              label={formatMessage('Admin.Enterprise.Fields.MemberID')}
              placeholder={formatMessage('Admin.Enterprise.UidPlaceholder')}
            />
          )}

          {userHasPermission(user, PERMISSIONS.ADMIN_SECTION_SHOW_USER_NAME_ID) && (
            <FormInput
              name="nameId"
              required={!!initialValues.nameId}
              label={formatMessage('Admin.Enterprise.Fields.NameID')}
              placeholder={formatMessage('Admin.Enterprise.NameIdPlaceholder')}
            />
          )}

          <div className="form-actions d-flex justify-content-end">
            <CancelButton onClick={this.props.onCancel} />

            <PrimaryButton type="submit" disabled={!this.canSubmit()}>
              <FormattedMessage id={modifyButtonTextKey} />
            </PrimaryButton>
          </div>
        </Form>
      </div>
    );
  }
}

const EnterpriseMemberSchema = Yup.object().shape({
  uid: Yup.string(),
  nameId: Yup.string(),
});

const formikWrapper = withFormik<Props, FormProps>({
  validationSchema: EnterpriseMemberSchema,
  mapPropsToValues: props => ({ ...props.memberToUpdate }),
  handleSubmit: (values, { props, setSubmitting, resetForm }) => {
    props.onCancel();
    props
      .onModifyMember(props.memberToUpdate._id, values.uid, values.nameId)
      .then(() => {
        notification.success(formatMessage(props.successTextKey));
        resetForm();
      })
      .finally(() => {
        setSubmitting(false);
      });
  },
});

export default formikWrapper(ModifyEnterpriseMemberForm) as any;
